import { graphql, useStaticQuery } from "gatsby";
import * as React from "react";
import { useEffect } from "react";
import { Header } from "../components/layout/header";
import { Layout } from "../components/layout/layout";
import { WeatherSearchMainForm } from "../components/search/forms/weather-search-main";
import { AppMap } from "../components/search/map/map";
import { MultiLineButton } from "../components/shared/base/multiline-button";
import {
    DestinationCardsSearch,
    InsideCardsTextWrapper
} from "../components/shared/cards";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { useAppSelector } from "../hooks/useAppSelector";
import { useElementSize } from "../hooks/useElementSize";
import { useUrlParams } from "../hooks/useUrlParams";
import { LocationType } from "../interfaces";
import isBrowser from "../services/isBrowser";
import {
    RegionBaseWithImage,
    WeatherSearchResultRowComplete,
    initStaticRegions
} from "../services/weatherSearch";
import {
    toggleResponsiveView,
    weatherSearchFormInit,
    weatherSearchSlice
} from "../store/weatherSearchSlice";
const WeatherSearchPage = ({
    location,
    pageContext
}: {
    location: LocationType;
    pageContext: { lang?: string };
}) => {
    return (
        <Layout
            path="/weather-search/"
            og="og-weather-search.png"
            title={"Weather search"}
            description={"Weather search"}
            switchTitleOrder={true}
            noHeaderFooter={true}
            rootClasses="overflow-x-hidden"
        >
            <Header noFadedEdge={false} />
            <WeatherSearchPageInternal />
        </Layout>
    );
};

const WeatherSearchPageInternal = () => {
    const data: RegionBaseWithImage[] = useStaticQuery(graphql`
        {
            allRegionsJson {
                nodes {
                    ...RegionBase
                    mainImgData: mainImg {
                        url: relativePath
                    }
                }
            }
        }
    `).allRegionsJson.nodes;

    useUrlParams();
    const dispatch = useAppDispatch();
    const results = useAppSelector(
        (state) => state.weatherSearch.searchResultsExtended
    );
    const resultsForCards = useAppSelector(
        (state) => state.weatherSearch.searchResultsForCards
    );
    const inInteraction = useAppSelector((state) => {
        return state.weatherSearch.inInteraction;
    });
    const showMapResultsMobile = useAppSelector((state) => {
        return state.weatherSearch.showMapResultsMobile;
    });
    const width = useElementSize({});
    const height = useElementSize({ useHeight: true });
    const initialized = React.useRef(false);
    const [widthType, setWidthType] = React.useState<string>();
    const [finalResultsForCards, setFinalResultsForCards] =
        React.useState<WeatherSearchResultRowComplete[]>();
    const [additionalResultsForCards, setAdditionalResultsForCards] =
        React.useState<WeatherSearchResultRowComplete[]>();

    useEffect(() => {
        if (!inInteraction) {
            setFinalResultsForCards(resultsForCards);
            if (
                resultsForCards &&
                resultsForCards.length < 6 &&
                widthType == "desktop"
            ) {
                const slugs = resultsForCards.map((r) => r.slug);
                const additionalResults = [];
                for (let result of results) {
                    if (slugs.includes(result.slug)) {
                        continue;
                    }
                    additionalResults.push(result);
                    if (
                        additionalResults.length + resultsForCards.length >=
                        12
                    ) {
                        break;
                    }
                }
                setAdditionalResultsForCards(additionalResults);
            } else {
                setAdditionalResultsForCards(null);
            }
        }
        if (
            inInteraction &&
            resultsForCards?.length &&
            !finalResultsForCards?.length
        ) {
            setFinalResultsForCards(resultsForCards);
        }
    }, [resultsForCards, inInteraction]);

    useEffect(() => {
        if (!width || initialized.current) return;
        initialized.current = true;
        initStaticRegions(data);
        dispatch(weatherSearchFormInit(true));
    });

    useEffect(() => {
        if (!width) return;
        const wType = width <= 991 ? "mobile" : "desktop";
        setWidthType(wType);
        dispatch(toggleResponsiveView(wType == "mobile"));
    }, [width]);

    useEffect(() => {
        if (!height || !isBrowser() || widthType != "mobile") {
            return;
        }
        document.documentElement.style.setProperty("--ih", `${height}px`);
    }, [height, widthType]);

    // TODO locale
    return (
        <div
            className={`relative pt-[62px] md:pt-[65px] ${
                widthType == "mobile"
                    ? "h-[calc(var(--ih,_100%)_-_0px)]"
                    : "h-[calc(100%_-_0x)]"
            } md:h-[calc(100vh_-_0px)]`}
        >
            {widthType == "desktop" && (
                <div className="relative flex h-[calc(100vh_-_65px)] justify-start pt-4 pb-[8px] mdmax:hidden">
                    <div className="shadow-standard relative flex-1 basis-[310px] rounded-md">
                        <div className="scrollbar-indent scrollbar-rounded absolute h-full w-full overflow-y-auto px-[16px] scrollbar-thin scrollbar-thumb-zinc-200">
                            <WeatherSearchMainForm className="pt-[16px]" />
                        </div>
                        <div className="scrollable-search-fade pointer-events-none absolute h-full w-full" />
                    </div>
                    <div
                        className={`scrollable-search-fade scrollbar-indent shadow-standard  ml-[20px] mr-[20px] w-[344px] flex-shrink-0 overflow-y-auto rounded-md py-[10px] pl-[10px] scrollbar-thin scrollbar-thumb-zinc-200 ${
                            !finalResultsForCards?.length &&
                            "flex justify-center"
                        }`}
                    >
                        {results?.length ? (
                            <DestinationCardsSearch
                                regions={finalResultsForCards?.slice(0, 40)}
                                additionalRegions={additionalResultsForCards}
                            />
                        ) : (
                            <div className="items-center self-center text-center">
                                <NoResultsText
                                    loading={!results}
                                    noResultsAtAll={true}
                                />
                            </div>
                        )}
                    </div>
                    <div className="shadow-standard flex-1 basis-2/5 rounded-md">
                        <AppMap
                            fromMainSearchForm={true}
                            className="rounded-md"
                        />
                    </div>
                </div>
            )}
            {widthType == "mobile" && (
                <div className="relative h-full md:hidden">
                    <div className=" relative h-[calc(100%-74px)] overflow-y-auto">
                        {showMapResultsMobile ? (
                            <>
                                <div className=" h-[calc(100%-206px)]">
                                    <AppMap fromMainSearchForm={true} />
                                </div>
                                {finalResultsForCards?.length ? (
                                    <DestinationCardsSearch
                                        className={`p-[4px] ${
                                            finalResultsForCards.length <= 2 &&
                                            "justify-center"
                                        }`}
                                        regions={finalResultsForCards.slice(
                                            0,
                                            30
                                        )}
                                        inRow={true}
                                    />
                                ) : (
                                    <div className={`flex h-[160px]`}>
                                        <div className="mt-[48px] w-full items-center self-center">
                                            <NoResultsText
                                                loading={!results}
                                                noResultsAtAll={
                                                    !results?.length
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <WeatherSearchMainForm
                                className="px-[20px] pb-[20px] pt-[8px]"
                                hideNumberOfResults={true}
                            />
                        )}
                    </div>
                    <div className="shadow-standard h-[74px] w-full px-4 py-[8px]">
                        <MultiLineButton
                            className="m-auto h-full w-full max-w-[400px] content-center items-center justify-center leading-loose"
                            onClick={() => {
                                dispatch(
                                    weatherSearchSlice.actions.toggleShowMapResultsMobile(
                                        !showMapResultsMobile
                                    )
                                );
                            }}
                            firstLine={
                                showMapResultsMobile
                                    ? "Change weather search parameters"
                                    : "Find holiday"
                            }
                            secondLine={
                                showMapResultsMobile
                                    ? undefined
                                    : `Explore ${results?.length} destinations`
                            }
                        />
                    </div>
                </div>
            )}
            {/* <div className="h-[40px]">Footer</div> */}
        </div>
    );
};

export default WeatherSearchPage;

const NoResultsText = ({
    loading,
    noResultsAtAll
}: {
    loading: boolean;
    noResultsAtAll: boolean;
}) => {
    // TODO locale
    return (
        <InsideCardsTextWrapper>
            {loading
                ? "Loading..."
                : noResultsAtAll
                ? "No results were found, please refine your search in the filters."
                : "No results were found, please zoom the map."}
        </InsideCardsTextWrapper>
    );
};
